import { useEffect, useState } from "react";
import { apiTech } from "@/lib/api";
import { LogOut, Phone, Mail, Wrench, Award } from "lucide-react";

export default function TechProfile({ me, onLogout, onToggleDuty }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    apiTech.get("/tech/me/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-3">
      {/* Identity card */}
      <section className="bg-[#002060] border border-[#FFFFFF]/15 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#FF6600]/10 text-[#FF6600] grid place-items-center font-bold text-lg">
            {(me?.name || "T").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-[#FFFFFF] truncate">{me?.name || "Loading…"}</div>
            <div className="text-xs text-[#FFFFFF]/70 mt-0.5">Field Technician</div>
          </div>
          <button
            type="button"
            onClick={onToggleDuty}
            data-testid="tech-profile-duty"
            className={`ml-auto inline-flex items-center gap-2 h-9 px-3 rounded-full text-xs font-semibold transition border ${
              me?.on_duty
                ? "bg-[#FF6600] border-[#FF6600] text-[#FFFFFF]"
                : "bg-transparent border-[#FFFFFF]/30 text-[#FFFFFF]/70"
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${me?.on_duty ? "bg-[#FFFFFF]" : "bg-[#FFFFFF]/40"}`} />
            {me?.on_duty ? "On duty" : "Off duty"}
          </button>
        </div>
        <div className="mt-4 space-y-2 text-sm">
          {me?.phone && (
            <Row icon={Phone} label="Phone" value={me.phone} href={`tel:${me.phone}`} />
          )}
          {me?.email && (
            <Row icon={Mail} label="Email" value={me.email} href={`mailto:${me.email}`} />
          )}
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#002060] border border-[#FFFFFF]/15 rounded-lg p-4">
        <div className="text-[11px] uppercase tracking-wider text-[#FFFFFF]/70 font-semibold mb-3">
          This week
        </div>
        <div className="grid grid-cols-2 gap-3">
          <StatBox label="Jobs completed" value={stats?.week_count ?? "—"} />
          <StatBox
            label="Revenue"
            value={stats ? `$${Math.round(stats.week_revenue)}` : "—"}
            tone="emerald"
          />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <StatBox label="Today's jobs" value={stats?.today_count ?? "—"} small />
          <StatBox
            label="Today's revenue"
            value={stats ? `$${Math.round(stats.today_revenue)}` : "—"}
            tone="emerald"
            small
          />
        </div>
      </section>

      {/* Skills */}
      {me?.skills?.length > 0 && (
        <section className="bg-[#002060] border border-[#FFFFFF]/15 rounded-lg p-4">
          <div className="text-[11px] uppercase tracking-wider text-[#FFFFFF]/70 font-semibold mb-3 flex items-center gap-2">
            <Award className="w-3.5 h-3.5" /> Certified on
          </div>
          <div className="flex flex-wrap gap-1.5">
            {me.skills.map((s) => (
              <span
                key={s}
                className="inline-flex items-center gap-1 text-xs bg-[#002060] text-[#FFFFFF] px-2.5 py-1 rounded-full capitalize"
              >
                <Wrench className="w-3 h-3 text-[#FFFFFF]/70" /> {s}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Logout */}
      <button
        type="button"
        onClick={onLogout}
        data-testid="tech-logout"
        className="w-full bg-[#002060] border border-[#FFFFFF]/15 hover:bg-[#FFFFFF]/10 active:scale-[0.99] text-[#FFFFFF] rounded-lg p-3.5 flex items-center justify-center gap-2 font-medium transition"
      >
        <LogOut className="w-4 h-4" /> Sign out
      </button>

      <p className="text-center text-[11px] text-[#FFFFFF]/45 pt-2">
        Space Appliance Repair · v1
      </p>
    </div>
  );
}

function Row({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="flex items-center gap-2.5 py-1.5">
      <Icon className="w-4 h-4 text-[#FFFFFF]/45" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[#FFFFFF]/70 font-medium">{label}</div>
        <div className="text-sm text-[#FFFFFF] truncate">{value}</div>
      </div>
    </div>
  );
  if (href) return <a href={href} className="block hover:bg-[#FFFFFF]/10 -mx-2 px-2 rounded-md transition-colors">{inner}</a>;
  return inner;
}

function StatBox({ label, value, tone = "slate", small = false }) {
  const colors = { slate: "text-[#FFFFFF]", emerald: "text-[#FFFFFF]" };
  return (
    <div className={`bg-[#002060] rounded-md ${small ? "p-2.5" : "p-3"}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#FFFFFF]/70 font-medium">{label}</div>
      <div className={`display ${small ? "text-lg" : "text-2xl"} font-semibold num mt-0.5 ${colors[tone]}`}>
        {value}
      </div>
    </div>
  );
}
